import type { LucideIcon } from "lucide-react";
import type { KeyboardEvent, MouseEvent, MouseEventHandler } from "react";
import { CheckCheck } from "lucide-react";

import { cn } from "@/modern/utils";

import { SURFACE_BACK_BTN_CLS } from "../constants/layout";
import { updatesIdleEmptyCopy, type UpdatesInboxMode } from "../utils/timeWindow";
import { NotificationTileButton } from "./NotificationTileButton";

export type NotificationCaughtUpMarkAll = {
  markedCount: number;
  nextSurfaceLabel: string;
};

function markAllCaption(ack: NotificationCaughtUpMarkAll): string {
  const marked =
    ack.markedCount <= 0
      ? "You're all clear here"
      : ack.markedCount === 1
        ? "1 update marked as seen"
        : `${ack.markedCount} updates marked as seen`;
  return `${marked}. Taking you back to ${ack.nextSurfaceLabel}…`;
}

export function SeeEarlierUpdatesButton({
  onClick,
  className,
  nested = false,
}: {
  onClick: MouseEventHandler<HTMLElement>;
  className?: string;
  /** Inside a tile `<button>` — render a span to avoid nested buttons. */
  nested?: boolean;
}) {
  const cls = cn(SURFACE_BACK_BTN_CLS, className);
  if (nested) {
    return (
      <span
        role="button"
        tabIndex={0}
        className={cls}
        onClick={onClick}
        onKeyDown={(event: KeyboardEvent<HTMLSpanElement>) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          event.stopPropagation();
          onClick(event as unknown as MouseEvent<HTMLElement>);
        }}
      >
        See earlier updates
      </span>
    );
  }
  return (
    <button type="button" className={cls} onClick={onClick}>
      See earlier updates
    </button>
  );
}

/** Calm caught-up empty — full-bleed mosaic tile filling the detail surface. */
export function NotificationCaughtUpEmpty({
  eyebrow,
  title = "You're caught up here",
  caption,
  markAllAck,
  onBack,
  inboxMode = "unread",
  onSeeEarlier,
  classes,
  icon: Icon = CheckCheck,
  className,
}: {
  eyebrow: string;
  title?: string;
  /** Default idle empty copy. Ignored when `markAllAck` is set. */
  caption: string;
  /** After mark-all: same tile, adjusted caption, then auto-navigate back. */
  markAllAck?: NotificationCaughtUpMarkAll | null;
  onBack: () => void;
  inboxMode?: UpdatesInboxMode;
  onSeeEarlier?: () => void;
  classes: { bg: string; text: string; ring: string };
  icon?: LucideIcon;
  className?: string;
}) {
  const idle = updatesIdleEmptyCopy(inboxMode, { title, caption });
  const resolvedCaption = markAllAck ? markAllCaption(markAllAck) : idle.caption;
  const showSeeEarlier = inboxMode === "unread" && Boolean(onSeeEarlier) && !markAllAck;

  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 items-stretch justify-stretch",
        className,
      )}
      role={markAllAck ? "status" : undefined}
      aria-live={markAllAck ? "polite" : undefined}
    >
      <NotificationTileButton
        onClick={onBack}
        classes={classes}
        span="h-full min-h-0 w-full flex-1"
        className="h-full min-h-[16rem] w-full p-6 sm:p-8"
        eyebrow={eyebrow}
        eyebrowClassName="text-sm tracking-[0.16em] sm:text-base"
        value={idle.title}
        valueClassName="text-3xl sm:text-5xl"
        caption={showSeeEarlier ? undefined : resolvedCaption}
        captionClassName="text-base leading-snug sm:text-lg"
        icon={Icon}
      >
        {showSeeEarlier && onSeeEarlier ? (
          <>
            <SeeEarlierUpdatesButton
              nested
              className="mt-3"
              onClick={(event) => {
                event.stopPropagation();
                onSeeEarlier();
              }}
            />
            <p className="mt-1 text-base font-medium leading-snug text-foreground/75 sm:text-lg">
              {resolvedCaption}
            </p>
          </>
        ) : null}
      </NotificationTileButton>
    </div>
  );
}
